import { Box, Input, Label } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { InterfaceNameValue } from '@tiendanube/components';
import { RadioButtonGroup, Stack } from 'commons/components';
import { getTodayIsoString } from 'commons/utils/date';
import { CouponDateType } from 'domains/Marketing/Coupons/components/CouponForm/types';
import {
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
} from 'domains/Marketing/Coupons/TimeZoneConstants';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { RadioOptionsSkeleton } from '../RadioOptionsSkeleton';

export interface DatesValuesInterface {
  dateType: CouponDateType;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export interface DatesErrorsInterface {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}

interface DatesProps {
  values: DatesValuesInterface;
  errors?: Partial<DatesErrorsInterface>;
  onChange: ({ value, name }: InterfaceNameValue) => void;
}

export function Dates({
  values: {
    dateType,
    startDate,
    startTime = DEFAULT_START_TIME,
    endDate,
    endTime = DEFAULT_END_TIME,
  },
  errors,
  onChange,
}: Readonly<DatesProps>) {
  const t = useTranslationMarketing('coupons.couponForm.limits.date');
  const {
    startDate: startDateError,
    endDate: endDateError,
    startTime: startTimeError,
    endTime: endTimeError,
  } = errors || {};
  const today = getTodayIsoString();

  const handleOnChange = ({ target: { name, value } }) =>
    onChange({ name, value });

  return (
    <Stack column align="stretch" spacing="tight">
      <Stack.Item fill>
        <RadioButtonGroup
          label={t('title')}
          boldLabel
          name="dateType"
          options={[
            {
              label: t('type.unlimited'),
              value: 'unlimited',
            },
            {
              label: t('type.limited'),
              value: 'limited',
            },
          ]}
          onChange={onChange}
          value={dateType}
        />
      </Stack.Item>
      {dateType === 'limited' && (
        <Stack.Item fill>
          <Box
            paddingTop="3"
            display="grid"
            gridTemplateColumns={{ xs: '100fr', md: '100fr' }}
          >
            <Box>
              <Label>{t('start')}</Label>
              <Box
                paddingTop="1"
                display="grid"
                gridTemplateColumns={{ xs: '50fr 50fr', md: '50fr 50fr' }}
              >
                <Box paddingRight="2">
                  <FormField id="startDate">
                    <Input
                      type="date"
                      name="startDate"
                      value={startDate}
                      min={today}
                      onChange={handleOnChange}
                      appearance={startDateError ? 'danger' : undefined}
                      id="startDate"
                    />
                  </FormField>
                </Box>
                <Box paddingLeft="2">
                  <FormField id="startTime">
                    <Input
                      type="time"
                      name="startTime"
                      value={startTime}
                      onChange={handleOnChange}
                      id="timeStart"
                      appearance={startTimeError ? 'danger' : undefined}
                    />
                  </FormField>
                </Box>
              </Box>
            </Box>

            <Box paddingTop="3">
              <Label>{t('end')}</Label>
              <Box
                paddingTop="1"
                display="grid"
                gridTemplateColumns={{ xs: '50fr 50fr', md: '50fr 50fr' }}
              >
                <Box paddingRight="2">
                  <FormField id="endDate">
                    <Input
                      type="date"
                      name="endDate"
                      value={endDate}
                      min={startDate}
                      onChange={handleOnChange}
                      appearance={endDateError ? 'danger' : undefined}
                      id="endDate"
                    />
                  </FormField>
                </Box>
                <Box paddingLeft="2">
                  <FormField
                    id="endTime"
                    helpText={t(endTimeError || '')}
                    showHelpText={
                      !!endTimeError && endTimeError.endsWith('endAfterStart')
                    }
                    appearance={endTimeError ? 'danger' : undefined}
                  >
                    <Input
                      type="time"
                      name="endTime"
                      value={endTime}
                      onChange={handleOnChange}
                      appearance={endTimeError ? 'danger' : undefined}
                      id="endTime"
                    />
                  </FormField>
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack.Item>
      )}
    </Stack>
  );
}

Dates.Skeleton = RadioOptionsSkeleton;
